import { useFlag } from '@calendly/feature-check-react'
import { createContext, useCallback, useContext, useEffect, useMemo, useState } from 'react'

import { useGetConnectedIntegrationsQuery } from 'features/integrations'
import {
  useFetchHubspotAccountForMiQuery,
  useFetchSalesforceAccountForMiQuery,
} from 'features/meeting_intelligence/api'
import { ALL_RECORDING_RULES_INTEGRATIONS, INTEGRATION_WITHOUT_TOGGLE } from 'features/meeting_intelligence/constants'

export const IntegrationsContext = createContext()

export const IntegrationsProvider = ({
  children,
}) => {
  // Fetch connected integrations list
  const {
    data: connectedIntegrationsData,
    isLoading: isConnectedIntegrationsLoading,
  } = useGetConnectedIntegrationsQuery()

  // Check if hubspot and salesforce are connected (integrations in INTEGRATION_WITHOUT_TOGGLE have no toggle, only connection status)
  const isHubspotConnected = connectedIntegrationsData?.some(
    (integration) => integration.name === 'hubspot' && integration.connected
  )
  const isSalesforceConnected = connectedIntegrationsData?.some(
    (integration) => integration.name === 'salesforce' && integration.connected
  )

  // Conditionally fetch integration account data (HubSpot and Salesforce only; integrations in INTEGRATION_WITHOUT_TOGGLE have no toggle)
  const {
    data: hubspotAccountData,
    isLoading: isHubspotAccountLoading,
    refetch: refetchHubspotAccount,
  } = useFetchHubspotAccountForMiQuery(undefined, {
    skip: !isHubspotConnected,
  })

  const {
    data: salesforceAccountData,
    isLoading: isSalesforceAccountLoading,
    refetch: refetchSalesforceAccount,
  } = useFetchSalesforceAccountForMiQuery(undefined, {
    skip: !isSalesforceConnected,
  })

  const isSalesforcePackageOutdated = isSalesforceConnected
    && salesforceAccountData?.packageVersion
    && !salesforceAccountData?.isMeetingNotesSyncSupported
  const zapierCardEnabled = useFlag('integrations_card_zapier')

  // State management
  const [localIntegrations, setLocalIntegrations] = useState([])
  const [originalIntegrations, setOriginalIntegrations] = useState([])
  // Combine loading states (no account fetch for INTEGRATION_WITHOUT_TOGGLE)
  const isLoading = isConnectedIntegrationsLoading
    || (isHubspotConnected && isHubspotAccountLoading)
    || (isSalesforceConnected && isSalesforceAccountLoading)

  // Build integrations array from fetched data
  useEffect(() => {
    if (!connectedIntegrationsData) return

    const integrations = connectedIntegrationsData
      .filter((integration) => ALL_RECORDING_RULES_INTEGRATIONS.includes(integration.name)
        && (integration.name !== 'zapier' || zapierCardEnabled))
      .map((integration) => {
        let enabled = false
        if (integration.name === 'hubspot' && hubspotAccountData) {
          enabled = hubspotAccountData.syncMiNotes ?? false
        } else if (integration.name === 'salesforce' && salesforceAccountData) {
          enabled = salesforceAccountData.meetingNotesEnabled ?? false
        }
        // Integrations in INTEGRATION_WITHOUT_TOGGLE have no toggle; enabled is unused for display

        return {
          name: integration.name,
          connected: integration.connected,
          enabled,
        }
      })

    setLocalIntegrations(integrations)
    setOriginalIntegrations(integrations)
  }, [
    connectedIntegrationsData,
    hubspotAccountData,
    salesforceAccountData,
    zapierCardEnabled,
  ])

  // Toggle integration enabled state
  const handleToggleIntegration = useCallback(
    (integrationName) => {
      if (INTEGRATION_WITHOUT_TOGGLE.includes(integrationName)) return
      setLocalIntegrations((prev) => prev.map((integration) => (
        integration.name === integrationName
          ? { ...integration, enabled: !integration.enabled }
          : integration
      )))
    },
    []
  )

  // Compare original integrations with localIntegrations to find changed ones
  const handleGatherIntegrations = useCallback(() => {
    const changedIntegrations = localIntegrations
      .filter((localIntegration) => {
        const originalIntegration = originalIntegrations.find(
          (orig) => orig.name === localIntegration.name
        )
        return originalIntegration
          && originalIntegration.enabled !== localIntegration.enabled
      })
      .filter((integration) => !INTEGRATION_WITHOUT_TOGGLE.includes(integration.name))
      .map((integration) => {
        const result = {
          name: integration.name,
          enabled: integration.enabled,
        }
        // Include existing HubSpot account data for the API call
        if (integration.name === 'hubspot' && hubspotAccountData) {
          result.syncEvents = hubspotAccountData.syncEvents
          result.syncStatus = hubspotAccountData.syncStatus
        }
        return result
      })

    return changedIntegrations
  }, [localIntegrations, originalIntegrations, hubspotAccountData])

  const handleCancelIntegrations = useCallback(() => {
    setLocalIntegrations([...originalIntegrations])
  }, [originalIntegrations])

  // Expose refetch functions for use after save (HubSpot and Salesforce only; INTEGRATION_WITHOUT_TOGGLE have no toggle to update)
  const refetchIntegrations = useCallback(async () => {
    if (isHubspotConnected) {
      await refetchHubspotAccount()
    }
    if (isSalesforceConnected) {
      await refetchSalesforceAccount()
    }
  }, [isHubspotConnected, isSalesforceConnected, refetchHubspotAccount, refetchSalesforceAccount])

  const contextValue = useMemo(
    () => ({
      integrations: localIntegrations,
      isLoading,
      isSalesforcePackageOutdated,
      zapierCardEnabled,
      handleGatherIntegrations,
      handleCancelIntegrations,
      handleToggleIntegration,
      refetchIntegrations,
    }),
    [
      localIntegrations,
      isLoading,
      isSalesforcePackageOutdated,
      zapierCardEnabled,
      handleGatherIntegrations,
      handleCancelIntegrations,
      handleToggleIntegration,
      refetchIntegrations,
    ]
  )

  return (
    <IntegrationsContext.Provider value={contextValue}>
      {children}
    </IntegrationsContext.Provider>
  )
}

export const useIntegrationsContext = () => useContext(IntegrationsContext)
